import {
  metricDefinitions,
  workloadBuckets,
  type PerformanceMetric,
  type TimingSummary,
} from './performanceSchema';
interface Aggregate {
  samples: number;
  slowSamples: number;
  totalMs: number;
  maxMs: number;
  workload: number;
}
/** Fixed-size counters; no object allocation or serialization on unsampled operations. */
export class PerformanceDiagnostics {
  private readonly states = new Map<
    PerformanceMetric,
    { active?: number; cooldown?: ReturnType<typeof setTimeout>; aggregate?: Aggregate }
  >(
    (Object.keys(metricDefinitions) as PerformanceMetric[]).map((metric) => [metric, {}]),
  );
  constructor(
    private readonly emit: (summary: TimingSummary) => void,
    private readonly clock = () => performance.now(),
  ) {}
  start(metric: PerformanceMetric): number | undefined {
    const state = this.states.get(metric);
    if (!state || state.active !== undefined || state.cooldown !== undefined) return;
    const now = this.clock();
    state.active = now;
    // Re-arm off the interaction path: suppressed calls perform no clock reads,
    // allocations, timer creation, serialization, or observable updates. Delayed
    // timers only reduce sampling frequency (e.g. in a background tab).
    state.cooldown = setTimeout(() => {
      state.cooldown = undefined;
    }, 1000);
    return now;
  }
  finish(metric: PerformanceMetric, started: number | undefined, workload?: number) {
    if (started === undefined) return;
    const state = this.states.get(metric);
    if (!state || state.active !== started) return;
    state.active = undefined;
    this.observe(metric, this.clock() - started, workload);
  }
  observe(metric: PerformanceMetric, duration: number, workload?: number) {
    const state = this.states.get(metric);
    if (!state || !Number.isFinite(duration) || duration < 0) return;
    const ms = Math.min(300000, Math.round(duration * 10) / 10);
    const bucket =
      workload === undefined || !Number.isFinite(workload) || workload < 0
        ? 0
        : workload === 0
          ? 1
          : workload < 100
            ? 2
            : workload < 1000
              ? 3
              : workload < 10000
                ? 4
                : 5;
    const aggregate = (state.aggregate ??= {
      samples: 0,
      slowSamples: 0,
      totalMs: 0,
      maxMs: 0,
      workload: 0,
    });
    if (aggregate.samples >= 100000) return;
    aggregate.samples++;
    if (ms >= metricDefinitions[metric].slowMs) aggregate.slowSamples++;
    aggregate.totalMs += ms;
    aggregate.maxMs = Math.max(aggregate.maxMs, ms);
    aggregate.workload = Math.max(aggregate.workload, bucket);
  }
  flush() {
    for (const [metric, state] of this.states) {
      const aggregate = state.aggregate;
      if (!aggregate) continue;
      this.emit({
        metric,
        samples: aggregate.samples,
        slowSamples: aggregate.slowSamples,
        totalMs: Math.round(aggregate.totalMs * 10) / 10,
        maxMs: aggregate.maxMs,
        workload: workloadBuckets[aggregate.workload],
      });
      state.aggregate = undefined;
    }
  }
  clear() {
    for (const state of this.states.values()) {
      state.aggregate = undefined;
      state.active = undefined;
      clearTimeout(state.cooldown);
      state.cooldown = undefined;
    }
  }
}
